import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Mail, Loader2, Home } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLOR_TO_FINISH = {
  "Obsidian Black": "obsidian_black",
  "Champagne Gold": "champagne_gold",
  "Chrome Silver": "chrome_silver",
};

/**
 * /success — Stripe Checkout redirect target.
 * Polls /api/checkout/session/{session_id} until the order flips to paid,
 * then renders a confirmation screen with the assigned founder number.
 */
export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState("loading"); // loading | paid | pending | not_found | error
  const [order, setOrder] = useState(null);

  useEffect(() => {
    document.title = "You're on the Founders list · Ivory Pulse";
  }, []);

  const poll = useCallback(
    async (attempt = 0) => {
      if (!sessionId) {
        setState("not_found");
        return;
      }
      try {
        const res = await axios.get(
          `${API}/checkout/session/${encodeURIComponent(sessionId)}`,
          { timeout: 15000 },
        );
        const data = res.data || {};
        if (!data.found) {
          if (attempt < 8) {
            setTimeout(() => poll(attempt + 1), 2000);
          } else {
            setState("not_found");
          }
          return;
        }
        setOrder(data.order || null);
        if ((data.order || {}).payment_status === "paid") {
          setState("paid");
          return;
        }
        if (attempt < 12) {
          setTimeout(() => poll(attempt + 1), 2500);
        } else {
          setState("pending");
        }
      } catch (e) {
        if (attempt < 6) {
          setTimeout(() => poll(attempt + 1), 3000);
        } else {
          setState("error");
        }
      }
    },
    [sessionId],
  );

  useEffect(() => {
    poll();
  }, [poll]);

  return (
    <div
      className="min-h-screen bg-black text-white relative overflow-hidden"
      data-testid="success-page"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-80"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(199,169,98,0.25) 0%, rgba(199,169,98,0.06) 35%, transparent 70%)",
        }}
      />
      <header className="relative border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="success-home-link"
            className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
          >
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Ivory Pulse"
              className="brand-invert h-8 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="text-xs uppercase tracking-[0.28em] text-white/50 hover:text-white transition-colors"
          >
            Back to Ivory Pulse
          </Link>
        </div>
      </header>

      <main className="relative max-w-2xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24 text-center">
        {state === "paid" && (
          <div data-testid="success-paid">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-white/15 bg-white/[0.04] mb-8"
              style={{
                boxShadow: "0 20px 60px -20px rgba(199,169,98,0.55)",
              }}
            >
              <Check className="w-7 h-7 text-[#e8d9b0]" />
            </div>
            <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#e8d9b0]">
              Founders Edition confirmed
            </p>
            <h1
              className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-medium tracking-[-0.03em] leading-[1.05]"
              data-testid="success-headline"
            >
              You're on the
              <br />
              <span className="text-white/60">Founders list.</span>
            </h1>
            <p className="mt-6 max-w-md mx-auto text-base md:text-lg text-white/60 leading-relaxed">
              Thank you for reserving your Pulse One — Founders Edition.
              We'll send launch and shipping updates to your email.
            </p>

            <div className="mt-12 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6 md:p-8 text-left">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {order?.founder_number ? (
                  <InfoItem
                    label="Founder number"
                    value={`#${String(order.founder_number).padStart(3, "0")} of 1,000`}
                    testId="success-founder-number"
                  />
                ) : null}
                {order?.selectedColor ? (
                  <InfoItem
                    label="Your finish"
                    value={order.selectedColor}
                    testId="success-color"
                  />
                ) : null}
                {order?.email ? (
                  <InfoItem
                    label="Confirmation email"
                    value={order.email}
                    testId="success-email"
                  />
                ) : null}
                {order?.amount_paid != null ? (
                  <InfoItem
                    label="Amount paid"
                    value={`${order.amount_paid.toFixed(2)} ${(order.currency || "USD").toUpperCase()}`}
                    testId="success-amount"
                  />
                ) : null}
              </div>
            </div>

            <div className="mt-10 flex flex-col items-center gap-4">
              <a
                href="mailto:support@ivory-pulse.com"
                data-testid="success-support"
                className="inline-flex items-center gap-2 text-sm text-white/75 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4" />
                support@ivory-pulse.com
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full bg-white text-black px-8 h-11 text-sm font-medium hover:bg-white/90 transition-all hover:scale-[1.02]"
              >
                <Home className="w-4 h-4" /> Back to home
              </Link>
            </div>
          </div>
        )}

        {(state === "loading" || state === "pending" || state === "not_found") && (
          <div data-testid="success-pending">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border border-white/15 bg-white/[0.04] mb-8">
              <Loader2 className="w-6 h-6 text-white/70 animate-spin" />
            </div>
            <h1
              className="font-display text-3xl md:text-4xl font-medium tracking-[-0.03em] leading-[1.1]"
            >
              Your payment was received.
            </h1>
            <p className="mt-5 max-w-md mx-auto text-base text-white/60 leading-relaxed">
              Confirmation details may take a few moments to appear. We've
              emailed a receipt — you can safely close this page or return to
              the home page.
            </p>
            <div className="mt-10 flex flex-col items-center gap-3">
              <a
                href="mailto:support@ivory-pulse.com"
                className="inline-flex items-center gap-2 text-sm text-white/75 hover:text-white transition-colors"
              >
                <Mail className="w-4 h-4" />
                support@ivory-pulse.com
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.02] px-6 h-10 text-sm text-white/80 hover:text-white hover:border-white/40 transition-all"
              >
                <Home className="w-4 h-4" /> Back to home
              </Link>
            </div>
          </div>
        )}

        {state === "error" && (
          <div data-testid="success-error">
            <h1 className="font-display text-3xl md:text-4xl font-medium tracking-[-0.03em] leading-[1.1]">
              We couldn't verify your order right now.
            </h1>
            <p className="mt-5 max-w-md mx-auto text-base text-white/60 leading-relaxed">
              If your card was charged, it will clear automatically. For help,
              email{" "}
              <a
                href="mailto:support@ivory-pulse.com"
                className="underline decoration-white/30 hover:decoration-white"
              >
                support@ivory-pulse.com
              </a>{" "}
              with your Stripe reference.
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-black px-6 h-10 text-sm font-medium hover:bg-white/90 transition-all"
            >
              <Home className="w-4 h-4" /> Back to home
            </Link>
          </div>
        )}

        <p className="mt-16 text-[11px] text-white/30 tracking-widest uppercase">
          Prototype shown. Final product specifications may vary.
        </p>
        {/* Hidden echo of the finish finish id to help debugging. */}
        {order?.selectedColor ? (
          <span className="sr-only" data-testid="success-finish-id">
            {COLOR_TO_FINISH[order.selectedColor] || ""}
          </span>
        ) : null}
      </main>
    </div>
  );
}

const InfoItem = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <p className="text-[10px] tracking-[0.28em] uppercase text-white/40">
      {label}
    </p>
    <p className="mt-2 text-base md:text-lg text-white">{value}</p>
  </div>
);
